/**
 * Рыночные примитивы SPECTER.
 *
 * Всё, что здесь объявлено, — уже нормализованные данные SPECTER Core.
 * Биржевые форматы (BTCUSDT, XBT/USD, интервал "1h") сюда не попадают:
 * они остаются внутри адаптера провайдера. См. ТЗ, раздел 8.3.
 */
import { timeframeMs } from './timeframe.js';
export function toTuple(c) {
    return [c.time, c.open, c.high, c.low, c.close, c.volume];
}
export function fromTuple(t) {
    return { time: t[0], open: t[1], high: t[2], low: t[3], close: t[4], volume: t[5] };
}
/**
 * Класс актива.
 *
 * Заполняет адаптер: только он знает, как биржа размечает инструменты.
 * `other` — источник разметки не даёт, и выдумывать её нельзя.
 *
 * `forex` в списке есть, хотя ни один из подключённых источников таких
 * инструментов пока не отдаёт: вкладка появится сама, когда появится
 * источник, а не будет висеть пустой.
 */
/**
 * Площадка рыночных индексов.
 *
 * Имя общее для Core и терминала: строка, продублированная в двух
 * местах, однажды разойдётся, и терминал начнёт искать TOTAL там, где
 * его нет.
 */
export const INDEX_VENUE = 'index';
/**
 * Рыночные индексы, которые платформа умеет считать.
 *
 * Список живёт здесь, а не в источнике данных, потому что его читают
 * трое: сам источник, проверка имени инструмента и терминал. Разъедься
 * они — Core отверг бы имя, которое сам же отдал в списке инструментов.
 *
 * Список короткий намеренно: каждый индекс здесь — обещание, что число
 * посчитано и объяснимо.
 */
export const INDEX_SYMBOLS = [
    {
        symbol: 'TOTAL',
        name: 'Капитализация рынка',
        hint: 'Общая капитализация всех криптовалют в долларах.',
        kind: 'total',
    },
    {
        /*
         * Именно USDT, а не «доллар вообще».
         *
         * Трейдеры говорят «USD.D», но имеют в виду доминирование Tether —
         * на TradingView это `CRYPTOCAP:USDT.D`. Классический индекс доллара
         * DXY к крипторынку отношения не имеет и считается совсем иначе.
         *
         * Сначала здесь стояла сумма долей всех долларовых стейблкоинов:
         * она давала 9.5% там, где привычное число — 6.8%, и человек,
         * знающий рынок, решил бы, что платформа считает неправильно. Имя в
         * данных обязано означать то, что посчитано.
         */
        symbol: 'USDT.D',
        name: 'Доминирование USDT',
        hint: 'Какую долю рынка занимает Tether. Растёт — деньги уходят в стейблкоин, а не в риск.',
        alias: 'USD.D',
        kind: 'usdt-dominance',
    },
    {
        /*
         * Капитализация берётся у источника напрямую, а не выводится из
         * доли.
         *
         * Соблазн был: доля × TOTAL ÷ 100 — и второй запрос не нужен. На
         * живых данных это дало 182.85 млрд против настоящих 183.38 — мимо
         * на полмиллиарда. Источник считает проценты по другой базе, чем
         * отдаёт в `total_market_cap`, и вывод из них даёт число, которое
         * не сойдётся ни с его же страницей, ни с чем-либо ещё.
         */
        symbol: 'USDT.MC',
        name: 'Капитализация USDT',
        hint: 'Сколько всего денег стоит в Tether. Растёт — на рынок заходят, но пока не покупают.',
        kind: 'usdt-cap',
    },
];
/**
 * Таймфреймы, на которых рыночный агрегат существует.
 *
 * Список живёт рядом с самими агрегатами, потому что читают его двое:
 * источник объявляет им свои возможности, а конструктор по нему
 * ограничивает выбор. Разъедься они — человек собрал бы условие на
 * `USDT.D · 5m`, оно молча не считалось бы никогда, и понять почему было
 * бы неоткуда.
 *
 * Минут здесь нет намеренно: источник обновляет срез раз в несколько
 * минут, и минутный бар по нему повторял бы одно значение шестьдесят
 * раз, выглядя при этом данными.
 */
export const INDEX_TIMEFRAMES = ['1H', '4H', '1D', '1W'];
/**
 * Это имя рыночного индекса?
 *
 * Проверка по списку, а не по форме имени. Формой не выйдет: `TOTAL`
 * ничем не отличается от `BTCUSDT`, кроме того, что первое мы считаем, а
 * второе — склеенный биржевой тикер, который человек вставил по ошибке.
 * Отличить их можно, только зная, что мы умеем.
 */
export function isIndexSymbol(name) {
    return INDEX_SYMBOLS.some((entry) => entry.symbol === name);
}
/**
 * Ближайший таймфрейм, на котором агрегат существует.
 *
 * Правило может стоять на пяти минутах, а срез рынка обновляется раз в
 * несколько минут: минутного ряда у источника нет и не будет. Условие
 * на несуществующем ряду не ошибается — оно молчит, и человек ищет
 * причину в самом условии.
 *
 * Вверх, а не вниз: старший таймфрейм у агрегата есть всегда, и «час»
 * вместо «пяти минут» — ближайшее, что можно посчитать честно.
 */
export function indexTimeframeFor(wanted) {
    if (INDEX_TIMEFRAMES.includes(wanted))
        return wanted;
    // По длительности, а не по списку: список забывал 2ч, 6ч, 12ч и 3д, и
    // для них бралось ближайшее снизу — час вместо суток.
    const size = timeframeMs(wanted);
    return (INDEX_TIMEFRAMES.find((tf) => timeframeMs(tf) >= size) ??
        INDEX_TIMEFRAMES[INDEX_TIMEFRAMES.length - 1] ??
        '1H');
}
//# sourceMappingURL=market.js.map